import { mapGetters, mapActions } from 'vuex';
import { CactusEventBus } from '@cactus-web/authenticator';
import { resetLocalStorage } from '../helpers/persistence';
import _has from 'lodash/has';

import LegalDocuments from '../components/_common/legalDocuments';
import Modal from '../components/_common/modal';
import MainNavigation from '../components/navigation/mainNavigation';
import FooterNavigation from '../components/navigation/footerNavigation';
import Brand from '../components/_common/brand';

export default {
  components: {
    LegalDocuments,
    Modal,
    MainNavigation,
    FooterNavigation,
    Brand
  },
  data() {
    return {
      currentUser: undefined
    };
  },

  computed: {
    ...mapGetters([
      'hasAcceptedPolicy',
      'hasAcceptedTerms',
      'hasAcceptedNews',
      'profile',
      'policyKey',
      'tcKey',
      'newsKey',
      'isAuthenticated',
      'policyDocument',
      'termsAndConditionDocument',
      'newsDocument',
      'deviceInterval'
    ]),

    errorRoute() {
      return this.$route.name === 'error';
    },
    showNav() {
      return this.$route.meta.showNav;
    }
  },

  created() {
    const html = document.documentElement;
    html.setAttribute('lang', 'de');
    this.cactusAuthenticate();
    this.getUser();

    // register an event listener to log user out from every tab
    // that might be open. Without it, a user can log out from one tab,
    // and get into an undefined state in the other tab.
    window.addEventListener('storage', event => {
      if (event.key == 'logout-event') {
        location.reload();
      }
    });
  },

  methods: {
    ...mapActions([
      'getProfile',
      'getPrivacyPolicyKey',
      'getTermsAndConditionsKey',
      'getNewsKey',
      'getPolicyDocument',
      'getTermsAndConditionDocument',
      'getNewsDocument'
    ]),

    resetAppState() {
      this.currentUser = undefined;
      // reset device state
      this.$store.dispatch('clearDevices');
      //reset cactursProfile state
      this.$store.dispatch('resetState');
      // ensure that our application is in a valid and functioning state after logout
      resetLocalStorage();

      // fire an event to ensure that user gets logged out from every tab
      localStorage.setItem('logout-event', 'logout' + Math.random());
      location.reload();
    },

    cactusAuthenticate() {
      CactusEventBus.$on('authState', info => {
        this.$log.info('App.js::cactusAuthenticate():', info);
        switch (info) {
          case 'signedIn':
            this.getUser();
            break;
          case 'signedOut':
            this.resetAppState();
            break;
        }
      });
    },

    /***
     * Function to get user object from authentication library. If we have a valid user,
     * we kick off the whole flow. If not user is detected, we have to ensure to clear
     * all data in localStorage. This is essential, as we store the device within it.
     */
    getUser() {
      this.$getCurrentUser().then(data => {
        if (!(data instanceof Error)) {
          this.currentUser = data;
          this.$store.dispatch('setUser', data);
          this.$store.dispatch('setIsAuthenticated', true);

          // before continuing to a certain route, first check if user has consent
          // to latest legal documents.
          this.$router.beforeResolve((to, from, next) => {
            this.checkUserLegalDocs().then(() => {
              next();
            });
          });
        } else if (data.message === 'not authenticated') {
          resetLocalStorage();
        }
      });
    },

    checkUserLegalDocs() {
      return Promise.all([
        this.getProfile(),
        this.getPrivacyPolicyKey(),
        this.getTermsAndConditionsKey(),
        this.getNewsKey()
      ]).then(() => {
        const POLICY_KEY = this.policyKey.key;
        const TC_KEY = this.tcKey.key;
        const NEWS_KEY = this.newsKey.key;

        // already registered
        if (_has(this.profile, 'privacy_policy') && _has(this.profile, 'terms_and_conditions')) {
          const userPolicyKey = this.profile.privacy_policy;
          const userTCKey = this.profile.terms_and_conditions;

          // document is updated
          if (userPolicyKey !== this.policyKey.latest || userTCKey !== this.tcKey.latest) {
            // document not updated
            if (userPolicyKey !== this.policyKey.latest) {
              const POLICY_KEY = this.policyKey.key;
              Promise.all([this.getPolicyDocument(POLICY_KEY)]);
            }
            if (userTCKey !== this.tcKey.latest) {
              Promise.all([this.getTermsAndConditionDocument(TC_KEY)]);
            }
          }
        } else {
          // document are missing
          if (!_has(this.profile, 'privacy_policy')) {
            Promise.all([this.getPolicyDocument(POLICY_KEY)]);
          }

          if (!_has(this.profile, 'terms_and_conditions')) {
            Promise.all([this.getTermsAndConditionDocument(TC_KEY)]);
          }
        }

        // What's New data
        if (_has(this.profile, 'news')) {
          const userNewsKey = this.profile.news;

          if (userNewsKey !== this.newsKey.latest) {
            Promise.all([this.getNewsDocument(NEWS_KEY)]);
          }
        } else {
          if (!_has(this.profile, 'news')) {
            Promise.all([this.getNewsDocument(NEWS_KEY)]);
          }
        }
      });
    }
  }
};
